import {
  eSaasComponents,
  SaasCreateFormPropContributors,
  SaasEditFormPropContributors,
} from '@volo/abp.ng.saas';
import { SaasTenantDto } from '@volo/abp.ng.saas/proxy';
import { ePropType, FormProp, FormPropList } from '@abp/ng.components/extensible';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

const ASSET_PASSWORD_PROP_NAME = 'AssetPassword';

const saasDisplayTextResolver = (key: string) => () => `SaasService::${key}`;

const confirmAdminPasswordValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const adminPassword = control.parent?.parent?.get('adminPassword')?.value;

  return control.value === adminPassword ? null : { passwordMismatch: true };
};

const tenantCreateProperties: FormProp<SaasTenantDto>[] = [
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'AdminUserName',
    displayName: 'DisplayName:AdminUserName',
    displayTextResolver: saasDisplayTextResolver('DisplayName:AdminUserName'),
    isExtra: true,
    validators: () => [Validators.required, Validators.maxLength(256)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'TradeName',
    displayName: 'TradeName',
    displayTextResolver: saasDisplayTextResolver('TradeName'),
    isExtra: true,
    validators: () => [Validators.required, Validators.maxLength(128)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'AccountNo',
    displayName: 'AccountNo',
    displayTextResolver: saasDisplayTextResolver('AccountNo'),
    isExtra: true,
    validators: () => [Validators.maxLength(32)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.Email,
    name: 'Email',
    displayName: 'Email',
    displayTextResolver: saasDisplayTextResolver('Email'),
    isExtra: true,
    validators: () => [Validators.required, Validators.email, Validators.maxLength(128)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'Www',
    displayName: 'Www',
    displayTextResolver: saasDisplayTextResolver('Www'),
    isExtra: true,
    validators: () => [Validators.maxLength(128)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'Phone',
    displayName: 'Phone',
    displayTextResolver: saasDisplayTextResolver('Phone'),
    isExtra: true,
    validators: () => [Validators.maxLength(32)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'AddressStreet1',
    group: { name: 'Address', className: 'mb-3' },
    displayName: 'AddressStreet1',
    displayTextResolver: saasDisplayTextResolver('AddressStreet1'),
    isExtra: true,
    validators: () => [Validators.maxLength(128)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'AddressStreet2',
    group: { name: 'Address', className: 'mb-3' },
    displayName: 'AddressStreet2',
    displayTextResolver: saasDisplayTextResolver('AddressStreet2'),
    isExtra: true,
    validators: () => [Validators.maxLength(128)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'AddressCity',
    group: { name: 'Address', className: 'mb-3' },
    displayName: 'AddressCity',
    displayTextResolver: saasDisplayTextResolver('AddressCity'),
    isExtra: true,
    validators: () => [Validators.maxLength(64)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'AddressRegion',
    group: { name: 'Address', className: 'mb-3' },
    displayName: 'AddressRegion',
    displayTextResolver: saasDisplayTextResolver('AddressRegion'),
    isExtra: true,
    validators: () => [Validators.maxLength(64)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'AddressPostCode',
    group: { name: 'Address', className: 'mb-3' },
    displayName: 'AddressPostCode',
    displayTextResolver: saasDisplayTextResolver('AddressPostCode'),
    isExtra: true,
    validators: () => [Validators.maxLength(32)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.String,
    name: 'AddressCountry',
    group: { name: 'Address', className: 'mb-3' },
    displayName: 'AddressCountry',
    displayTextResolver: saasDisplayTextResolver('AddressCountry'),
    isExtra: true,
    validators: () => [Validators.maxLength(64)],
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.Boolean,
    name: 'IsRequired',
    displayName: 'IsRequired',
    displayTextResolver: saasDisplayTextResolver('IsRequired'),
    isExtra: true,
    defaultValue: false,
  }),
  new FormProp<SaasTenantDto>({
    type: ePropType.PasswordInputGroup,
    name: 'ConfirmAdminPassword',
    displayName: 'ConfirmAdminPassword',
    displayTextResolver: saasDisplayTextResolver('ConfirmAdminPassword'),
    isExtra: true,
    validators: () => [
      Validators.required,
      Validators.maxLength(128),
      confirmAdminPasswordValidator,
    ],
  })
];

function replaceAssetPasswordProp(propList: FormPropList<SaasTenantDto>, displayName: string) {
  const index = propList.findIndex(node => node.value.name === ASSET_PASSWORD_PROP_NAME);

  if (index < 0) {
    return;
  }

  const current = propList.get(index);

  if (!current) {
    return;
  }

  propList.dropByIndex(index);
  propList.addByIndex(
    new FormProp<SaasTenantDto>({
      type: ePropType.PasswordInputGroup,
      name: ASSET_PASSWORD_PROP_NAME,
      displayName,
      displayTextResolver: saasDisplayTextResolver(displayName),
      isExtra: current.value.isExtra,
    }),
    index,
  );
}

export function assetPasswordCreatePropContributor(propList: FormPropList<SaasTenantDto>) {
  replaceAssetPasswordProp(propList, 'AssetPassword');
}

export function assetPasswordEditPropContributor(propList: FormPropList<SaasTenantDto>) {
  replaceAssetPasswordProp(propList, 'AssetPasswordEditHint');
}

export function tenantCreatePropertiesPropContributor(propList: FormPropList<SaasTenantDto>) {
  tenantCreateProperties.forEach(prop => {
    const index = propList.findIndex(node => node.value.name === prop.name);

    if (index < 0) {
      propList.addByIndex(prop, propList.length);
      return;
    }

    propList.dropByIndex(index);
    propList.addByIndex(prop, index);
  });
}

export const saasCreateFormPropContributors: SaasCreateFormPropContributors = {
  [eSaasComponents.Tenants]: [
    tenantCreatePropertiesPropContributor,
    assetPasswordCreatePropContributor,
  ],
};

export const saasEditFormPropContributors: SaasEditFormPropContributors = {
  [eSaasComponents.Tenants]: [assetPasswordEditPropContributor],
};
